import { Select, Tooltip } from 'antd'
import type { CIMSSelectProps } from './types'
import { useEffect } from 'react'
import { useFilterOption } from './hooks/useFilterOption'
import { useTagRender } from './hooks/useTagRender'
import { SearchOutlined } from '@ant-design/icons'

/**
 * the better version of the {@link Selector} component I built a while back
 *
 * this component is completely compatible with Form initialValues and should be
 * the preferred way to define select components in the application
 *
 * some considerations
 * - {@link CIMSSelectProps.disabled|disabled} is set by {@link CIMSSelectProps.loading|loading} or disabled
 * - prefer setting initial form values over default values when using a Form
 * - by default, values are assumed to be {@link LabeledValueType} objects to help
 *   deal with soft-deleted records, if you don't want this behavior, enable
 *   {@link CIMSSelectProps.simple|simple}
 * - {@link CIMSSelectProps.__debug|__debug} can be helpful
 *
 * @param props {@link CIMSSelectProps}
 */
const CIMSSelect: React.FC<CIMSSelectProps> = (props) => {
  const {
    autoClearSearchValue,
    simple,
    allowClear,
    __debug,
    children,
    disabled,
    style,
    virtual,
    ...rest
  } = props
  const tagRender = useTagRender(props)
  const filterOption = useFilterOption(props)

  useEffect(() => {
    if (props.__debug) console.log('CIMSSelect', { ...props })
  }, [props])
  return (
    <Select
      {...rest}
      /**
       * CIMSSelect options are wrapped in a Typography that prevents label text
       * from being properly muted by AntD styling when disabled.
       *
       * Fixing this directly would require some custom CSS, so instead we'll
       * just lower the opacity on the component when it's disabled.
       */
      style={{ ...style, opacity: disabled ? 0.6 : undefined }}
      labelInValue={!simple}
      disabled={disabled ?? props.loading}
      showSearch
      suffixIcon={
        props.loading ? undefined : (
          <Tooltip title="Search by option label, tooltip, or ID">
            <SearchOutlined />
          </Tooltip>
        )
      }
      autoClearSearchValue={autoClearSearchValue ?? false}
      showArrow
      /**
       * There's currently a bug in the Ant Design Select component that causes
       * the allowClear operation to also remove all disabled options, and they
       * cannot be added back after the fact because... they're disabled.
       *
       * To combat this we have to expose this prop and disable it when using
       * a multiple Select with disabled options.
       */
      allowClear={allowClear ?? true}
      filterOption={filterOption}
      tagRender={tagRender}
      /**
       * While virtual scrolling can improve scroll performance in selects with
       * many options, it can also be glitchy (scrollbar disappears, list cuts
       * short). Disabled by default for better UX unless there are 300+
       * options.
       */
      virtual={virtual ?? props.options.length > 300}
    />
  )
}

export default CIMSSelect
